/**
 * AnimatedNumber — zählt eine Kennzahl beim Erscheinen auf ihren Wert hoch.
 *
 * <p>Bei `prefers-reduced-motion: reduce` steht der Wert sofort. Das ist hier nicht nur
 * Höflichkeit: während der Animation zeigt die Kachel eine Zahl, die nicht stimmt — im
 * Blockrundgang stand auf demselben Apartment einmal „503 €" und einmal „702 €", je nachdem,
 * wann man hinsah.</p>
 */
import { animate, useMotionValue, useTransform, motion } from 'framer-motion';
import { useEffect } from 'react';
import { useReducedMotion } from '@/shared/hooks/useReducedMotion';

interface Props {
  value: number;
  format: (n: number) => string;
  /**
   * Dauer in Sekunden. Vorgabe ist `--duration-medium` (380 ms) statt der früheren 0,8 s: so
   * lange stand hier eine falsche Zahl, und §12 verlangt sparsame Bewegung für Oberflächen.
   */
  duration?: number;
  className?: string;
}

export function AnimatedNumber({ value, format, duration = 0.38, className }: Props) {
  const reduced = useReducedMotion();
  const motionValue = useMotionValue(reduced ? value : 0);
  const display = useTransform(motionValue, (latest) => format(latest));

  useEffect(() => {
    if (reduced) {
      motionValue.set(value);
      return;
    }
    const controls = animate(motionValue, value, {
      duration,
      ease: [0.16, 1, 0.3, 1],
    });
    return controls.stop;
  }, [value, duration, motionValue, reduced]);

  return <motion.span className={className}>{display}</motion.span>;
}
